import React, { useState } from 'react';
import { User, Membership } from '../types';
import { audioTracks } from '../data/mockData';
import { useLanguage } from '../i18n/LanguageContext';

interface AudioLibraryPageProps {
  user: User | null;
  navigate: (view: string, slug: string) => void;
}

type IntentionFilter = 'All' | 'Sleep' | 'Focus' | 'Relax' | 'Energy';
const intentions: IntentionFilter[] = ['All', 'Sleep', 'Focus', 'Relax', 'Energy'];

const intentionMap: Record<IntentionFilter, string> = {
    All: 'audioLibrary.intentions.all',
    Sleep: 'audioLibrary.intentions.sleep',
    Focus: 'audioLibrary.intentions.focus',
    Relax: 'audioLibrary.intentions.relax',
    Energy: 'audioLibrary.intentions.energy',
};


const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
    </svg>
);

const AudioLibraryPage: React.FC<AudioLibraryPageProps> = ({ user, navigate }) => {
  const [filter, setFilter] = useState<IntentionFilter>('All');
  const { t } = useLanguage();

  const filteredTracks = filter === 'All' 
    ? audioTracks 
    : audioTracks.filter(track => track.intention === filter);

  return (
    <div className="container mx-auto max-w-5xl">
      <h1 className="text-4xl font-bold mb-4 text-center">{t('audioLibrary.title')}</h1>
      <p className="text-center text-stone-600 mb-8">{t('audioLibrary.subtitle')}</p>
      
      <div className="flex justify-center flex-wrap gap-2 mb-10">
        {intentions.map(intention => (
          <button 
            key={intention}
            onClick={() => setFilter(intention)}
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${filter === intention ? 'bg-accent text-white' : 'bg-white/70 text-stone-700 hover:bg-white'}`}
          >
            {t(intentionMap[intention])}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredTracks.map(track => {
          const isLocked = track.is_premium && user?.membership !== Membership.Premium;
          return (
            <div 
              key={track.slug}
              className={`bg-white/60 p-6 rounded-3xl shadow-lg border border-white/50 flex flex-col justify-between ${isLocked ? 'opacity-70' : ''}`}
            >
              <div>
                <div className="flex justify-between items-start mb-2">
                    <h2 className="text-2xl font-bold">{track.title}</h2>
                    {isLocked && <LockIcon />}
                </div>
                <p className="text-sm font-medium bg-accent/20 text-accent px-3 py-1 rounded-full inline-block mb-4">{t(intentionMap[track.intention])}</p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500 font-medium">{track.duration_min} {t('modulesPage.minutes')}</span>
                <button
                  onClick={() => isLocked ? navigate('profile','') : alert(`Playing ${track.title}`)}
                  className={`px-5 py-2.5 font-medium text-center rounded-full text-sm transition-colors ${isLocked ? 'bg-stone-200 text-stone-500' : 'bg-accent text-white hover:bg-accent-dark'}`}
                >
                  {isLocked ? t('audioLibrary.upgrade') : t('audioLibrary.play')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AudioLibraryPage;